import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock } from 'lucide-react';

interface ArticleSchedulerProps {
  scheduledAt: string;
  onScheduledAtChange: (value: string) => void;
  timezone: string;
  onTimezoneChange: (value: string) => void;
}

export const ArticleScheduler: React.FC<ArticleSchedulerProps> = ({
  scheduledAt,
  onScheduledAtChange,
  timezone,
  onTimezoneChange
}) => {
  const timezones = [
    { value: 'UTC', label: 'UTC (GMT+0)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  ];

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Minimum 5 minutes from now
    return now.toISOString().slice(0, 16);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Schedule Publishing
        </CardTitle>
        <CardDescription>
          Set a specific date and time to automatically publish this article
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="scheduled-datetime">Publish Date & Time</Label>
          <Input
            id="scheduled-datetime"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => onScheduledAtChange(e.target.value)}
            min={getMinDateTime()}
          />
          <p className="text-xs text-muted-foreground">
            Article will automatically publish at this time
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={timezone} onValueChange={onTimezoneChange}>
            <SelectTrigger id="timezone">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {scheduledAt && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">Scheduled for:</p>
            <p className="text-sm text-muted-foreground">
              {new Date(scheduledAt).toLocaleString('en-US', {
                timeZone: timezone,
                dateStyle: 'full',
                timeStyle: 'short'
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
